import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "../../api/axios";
import YouTube from "react-youtube";
import Nav from "../../components/Navbar/Nav";
import MovieCard from "../../components/Movies/Movie/MovieCard";
import apiUrl from "../../api/config.json";
import "./CategoryBrowse.css";

const API_KEY = apiUrl.APIKey;

/* ── Genre map (movie IDs from TMDB) ── */
const GENRE_MAP = {
	28: "Action", 12: "Adventure", 16: "Animation", 35: "Comedy", 80: "Crime",
	99: "Documentary", 18: "Drama", 10751: "Family", 14: "Fantasy", 36: "History",
	27: "Horror", 10402: "Music", 9648: "Mystery", 10749: "Romance", 878: "Sci-Fi",
	10770: "TV Movie", 53: "Thriller", 10752: "War", 37: "Western",
	10759: "Action & Adventure", 10762: "Kids", 10763: "News",
	10764: "Reality", 10765: "Sci-Fi & Fantasy", 10766: "Soap",
	10767: "Talk", 10768: "War & Politics",
};

const SORT_OPTIONS = [
	{ value: "popularity.desc", label: "Most Popular" },
	{ value: "popularity.asc", label: "Least Popular" },
	{ value: "vote_average.desc", label: "Highest Rated" },
	{ value: "vote_average.asc", label: "Lowest Rated" },
	{ value: "primary_release_date.desc", label: "Newest First" },
	{ value: "primary_release_date.asc", label: "Oldest First" },
	{ value: "revenue.desc", label: "Highest Revenue" },
	{ value: "title.asc", label: "Title (A-Z)" },
];

const YEAR_OPTIONS = (() => {
	const years = [];
	const current = new Date().getFullYear();
	for (let y = current + 1; y >= 1990; y--) years.push(y);
	return years;
})();

const LANGUAGE_OPTIONS = [
	{ value: "", label: "All Languages" },
	{ value: "en", label: "English" },
	{ value: "ja", label: "Japanese" },
	{ value: "ko", label: "Korean" },
	{ value: "es", label: "Spanish" },
	{ value: "fr", label: "French" },
	{ value: "de", label: "German" },
	{ value: "hi", label: "Hindi" },
	{ value: "zh", label: "Chinese" },
	{ value: "it", label: "Italian" },
	{ value: "pt", label: "Portuguese" },
	{ value: "th", label: "Thai" },
	{ value: "tr", label: "Turkish" },
];

function CategoryBrowse() {
	const [searchParams, setSearchParams] = useSearchParams();
	const title = searchParams.get("title") || "Browse";
	const baseFetchUrl = searchParams.get("fetchUrl") || "";

	const [movies, setMovies] = useState([]);
	const [totalPages, setTotalPages] = useState(1);
	const [loading, setLoading] = useState(true);
	const [genres, setGenres] = useState([]);

	/* ── Trailer & active card state (same as Row component) ── */
	const [trailerUrl, setTrailerUrl] = useState("");
	const [trailerMessage, setTrailerMessage] = useState("");
	const [activeCard, setActiveCard] = useState(false);

	const trailerOpts = {
		width: "100%",
		playerVars: { autoplay: 1 },
	};

	function handleTrailerResponse(request) {
		if (request.data.results.length > 0) {
			setTrailerMessage("");
			const trailer = request.data.results.find((r) => r.type === "Trailer");
			setTrailerUrl(trailer?.key || "");
		} else {
			setTrailerMessage("No trailer found");
		}
	}

	const movieFocus = (movie) => {
		if (activeCard === movie.id) {
			setActiveCard(false);
		} else {
			setTrailerUrl("");
			setTrailerMessage("");
			setActiveCard(movie.id);
		}
	};

	const handleMovieClick = (movie) => {
		if (trailerUrl) {
			setTrailerUrl("");
			setTrailerMessage("");
		} else {
			async function fetchTrailer() {
				try {
					const request = await axios.get(
						`/movie/${movie.id}/videos?api_key=${API_KEY}&language=en-US`
					);
					handleTrailerResponse(request);
				} catch (err) {
					if (err.response) {
						const request = await axios.get(
							`/tv/${movie.id}/videos?api_key=${API_KEY}&language=en-US`
						);
						handleTrailerResponse(request);
					}
				}
			}
			fetchTrailer();
		}
	};

	/* ── Filters derived from URL search params ── */
	const page = parseInt(searchParams.get("page") || "1", 10);
	const sortBy = searchParams.get("sort_by") || "popularity.desc";
	const year = searchParams.get("year") || "";
	const language = searchParams.get("language") || "";
	const selectedGenres = searchParams.get("with_genres") || "";

	/* ── Detect if the endpoint is discover (supports sort/filter) ── */
	const isDiscover = useMemo(
		() => baseFetchUrl.includes("/discover/") || baseFetchUrl.includes("/trending/"),
		[baseFetchUrl]
	);

	/* ── Build paginated + filtered URL ── */
	const buildUrl = useCallback(() => {
		if (!baseFetchUrl) return "";

		let url = baseFetchUrl;

		/* Remove existing page param if present */
		url = url.replace(/&page=\d+/, "");

		/* For discover endpoints, inject sort & filter params */
		if (isDiscover) {
			/* Remove existing sort_by to replace it */
			url = url.replace(/&sort_by=[^&]+/, "");
			url += `&sort_by=${sortBy}`;

			if (year) url += `&primary_release_year=${year}`;
			if (language) url += `&with_original_language=${language}`;
			if (selectedGenres) url += `&with_genres=${selectedGenres}`;
		}

		url += `&page=${page}`;
		return url;
	}, [baseFetchUrl, isDiscover, sortBy, year, language, selectedGenres, page]);

	/* ── Fetch genre list once ── */
	useEffect(() => {
		async function fetchGenres() {
			try {
				const res = await axios.get(
					`/genre/movie/list?api_key=${searchParams.get("fetchUrl")?.match(/api_key=([^&]+)/)?.[1] || ""}&language=en-US`
				);
				if (res.data?.genres) {
					setGenres(res.data.genres);
				}
			} catch {
				/* Fallback: use static genre map */
				setGenres(
					Object.entries(GENRE_MAP).map(([id, name]) => ({
						id: parseInt(id),
						name,
					}))
				);
			}
		}
		fetchGenres();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	/* ── Fetch movies ── */
	useEffect(() => {
		if (!baseFetchUrl) return;
		let cancelled = false;

		async function fetchData() {
			setLoading(true);
			try {
				const url = buildUrl();
				const request = await axios.get(url);
				if (!cancelled) {
					const results = request.data.results || [];
					setMovies(results);
					setTotalPages(Math.min(request.data.total_pages || 1, 500)); // TMDB max 500
				}
			} catch (err) {
				console.error("Failed to fetch browse data:", err);
				if (!cancelled) {
					setMovies([]);
					setTotalPages(1);
				}
			} finally {
				if (!cancelled) setLoading(false);
			}
		}
		fetchData();

		return () => {
			cancelled = true;
		};
	}, [buildUrl, baseFetchUrl]);

	/* ── Param helpers ── */
	const setParam = (key, value) => {
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			if (value) {
				next.set(key, value);
			} else {
				next.delete(key);
			}
			/* Reset to page 1 when changing filters */
			if (key !== "page") next.set("page", "1");
			return next;
		});
	};

	const goToPage = (p) => {
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			next.set("page", String(p));
			return next;
		});
		window.scrollTo({ top: 0, behavior: "smooth" });
	};

	/* ── Genre toggle ── */
	const toggleGenre = (genreId) => {
		const current = selectedGenres ? selectedGenres.split(",").map(Number) : [];
		const updated = current.includes(genreId)
			? current.filter((g) => g !== genreId)
			: [...current, genreId];
		setParam("with_genres", updated.length ? updated.join(",") : "");
	};

	/* ── Pagination range builder ── */
	const getPageRange = () => {
		const delta = 2;
		const range = [];
		const left = Math.max(2, page - delta);
		const right = Math.min(totalPages - 1, page + delta);

		range.push(1);
		if (left > 2) range.push("...");
		for (let i = left; i <= right; i++) range.push(i);
		if (right < totalPages - 1) range.push("...");
		if (totalPages > 1) range.push(totalPages);
		return range;
	};

	const hasActiveFilters = selectedGenres || year || language || sortBy !== "popularity.desc";

	return (
		<div className="browse-page">
			<Nav />

			<div className="browse-hero">
				<div className="browse-hero__inner">
					<h1 className="browse-hero__title">{title}</h1>
					<p className="browse-hero__subtitle">
						{!loading && `${movies.length} results`}
						{totalPages > 1 && ` · Page ${page} of ${totalPages}`}
					</p>
				</div>
			</div>

			<div className="browse-content">
				{/* ── Filter Sidebar ── */}
				{isDiscover && (
					<aside className="browse-filters">
						<div className="browse-filters__header">
							<h3>Filters</h3>
							{hasActiveFilters && (
								<button
									className="browse-filters__clear"
									onClick={() => {
										setSearchParams((prev) => {
											const next = new URLSearchParams();
											next.set("title", prev.get("title") || "");
											next.set("fetchUrl", prev.get("fetchUrl") || "");
											return next;
										});
									}}
								>
									Clear All
								</button>
							)}
						</div>

						{/* Sort */}
						<div className="browse-filter-group">
							<label className="browse-filter-group__label">Sort By</label>
							<select
								className="browse-select"
								value={sortBy}
								onChange={(e) => setParam("sort_by", e.target.value)}
							>
								{SORT_OPTIONS.map((opt) => (
									<option key={opt.value} value={opt.value}>
										{opt.label}
									</option>
								))}
							</select>
						</div>

						{/* Year */}
						<div className="browse-filter-group">
							<label className="browse-filter-group__label">Release Year</label>
							<select
								className="browse-select"
								value={year}
								onChange={(e) => setParam("year", e.target.value)}
							>
								<option value="">All Years</option>
								{YEAR_OPTIONS.map((y) => (
									<option key={y} value={y}>
										{y}
									</option>
								))}
							</select>
						</div>

						{/* Language */}
						<div className="browse-filter-group">
							<label className="browse-filter-group__label">Language</label>
							<select
								className="browse-select"
								value={language}
								onChange={(e) => setParam("language", e.target.value)}
							>
								{LANGUAGE_OPTIONS.map((opt) => (
									<option key={opt.value} value={opt.value}>
										{opt.label}
									</option>
								))}
							</select>
						</div>

						{/* Genres */}
						<div className="browse-filter-group">
							<label className="browse-filter-group__label">Genre</label>
							<div className="browse-genre-chips">
								{genres.map((genre) => {
									const active = selectedGenres
										.split(",")
										.map(Number)
										.includes(genre.id);
									return (
										<button
											key={genre.id}
											className={`browse-genre-chip ${active ? "browse-genre-chip--active" : ""}`}
											onClick={() => toggleGenre(genre.id)}
										>
											{genre.name}
										</button>
									);
								})}
							</div>
						</div>
					</aside>
				)}

				{/* ── Main Grid ── */}
				<main className="browse-main">
					{loading ? (
						<div className="browse-grid">
							{Array.from({ length: 20 }).map((_, i) => (
								<div key={i} className="browse-skeleton">
									<div className="browse-skeleton__img" />
									<div className="browse-skeleton__text" />
									<div className="browse-skeleton__text browse-skeleton__text--short" />
								</div>
							))}
						</div>
					) : movies.length === 0 ? (
						<div className="browse-empty">
							<div className="browse-empty__icon">🎬</div>
							<h2>No results found</h2>
							<p>Try adjusting your filters or sorting options.</p>
						</div>
					) : (
						<>
							<div className="browse-grid">
								{movies.map(
									(movie) =>
										(movie.poster_path || movie.backdrop_path) && (
											<MovieCard
												movie={movie}
												key={movie.id}
												isActive={activeCard === movie.id}
												handleMovieClick={handleMovieClick}
												movieFocus={movieFocus}
											/>
										)
								)}
							</div>

							{trailerUrl && (
								<YouTube videoId={trailerUrl} id={trailerUrl} opts={trailerOpts} />
							)}
							{trailerMessage && (
								<div className="noTrailer">
									<h2>{trailerMessage}</h2>
								</div>
							)}

							{/* ── Pagination ── */}
							{totalPages > 1 && (
								<nav className="browse-pagination" aria-label="Pagination">
									<button
										className="browse-pagination__btn"
										disabled={page <= 1}
										onClick={() => goToPage(page - 1)}
									>
										&#8249; Prev
									</button>

									<div className="browse-pagination__pages">
										{getPageRange().map((p, i) =>
											p === "..." ? (
												<span key={`dots-${i}`} className="browse-pagination__dots">
													…
												</span>
											) : (
												<button
													key={p}
													className={`browse-pagination__page ${
														p === page ? "browse-pagination__page--active" : ""
													}`}
													onClick={() => goToPage(p)}
												>
													{p}
												</button>
											)
										)}
									</div>

									<button
										className="browse-pagination__btn"
										disabled={page >= totalPages}
										onClick={() => goToPage(page + 1)}
									>
										Next &#8250;
									</button>
								</nav>
							)}
						</>
					)}
				</main>
			</div>
		</div>
	);
}

export default CategoryBrowse;
